// Platform wrapper around the shared multi-tabular capture interface
// (@twasta/page-runtime/txn/capture). The shared component takes an injected
// CaptureRuntime (data adapter + form transport + layout persistence +
// ensureReady) and a versionKey; this wrapper curries in the designer
// runtime (embedDataApi + live-preview TxnRuntime + toast), the idempotent
// live-preview deploy, and the object-version store subscription — so every
// existing call site keeps its original props and behavior exactly.

import { useMemo, type ComponentProps } from 'react'
import { EmbedCaptureTabularPreview as CoreEmbedCaptureTabularPreview } from '@twasta/page-runtime/txn/capture'
import { useObjectVersion } from '@/store/objectVersionStore'
import { livePreviewDeploy } from '@/livepreview/api'
import { makeDesignerCaptureRuntime } from '@/livepreview/captureRuntime'

// Module-scope deploy tracker — shared across remounts so we don't
// redeploy the same schema repeatedly. Mirrors EmbedCapturePreview.
const deployedTxns = new Map<string, Promise<void>>()
function deployKey(project: string, obj: string) { return `${project}::${obj}` }

// Exported for the file-import views (assistant/ImportPreview), which render
// the SAME core grid with their own seed and must deploy it the same way.
export async function ensureDeployed(project: string, obj: string): Promise<void> {
  const key = deployKey(project, obj)
  const existing = deployedTxns.get(key)
  if (existing) return existing
  const p = livePreviewDeploy(project, obj).then(() => undefined)
  deployedTxns.set(key, p)
  try {
    await p
  } catch (err) {
    deployedTxns.delete(key)
    throw err
  }
}

type Props = Omit<
  ComponentProps<typeof CoreEmbedCaptureTabularPreview>,
  'runtime' | 'versionKey'
> & {
  // 'design' (object designer) bakes "Apply layout" into the shared core
  // design; 'customize' (7077 Run view) writes a per-tenant model overlay.
  layoutMode?: 'design' | 'customize'
}

export default function EmbedCaptureTabularPreview({ layoutMode = 'design', ...props }: Props) {
  const versionKey = useObjectVersion('T', props.project, props.embed.obj_name || '')
  const runtime = useMemo(
    () => makeDesignerCaptureRuntime(props.project, ensureDeployed, layoutMode),
    [props.project, layoutMode],
  )
  return (
    <CoreEmbedCaptureTabularPreview
      {...props}
      runtime={runtime}
      versionKey={versionKey}
    />
  )
}
